/**
 * Analytics utilities for tracking user interactions
 * Supports Google Analytics 4 (gtag), Vercel Analytics, and custom event tracking
 */

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {Object} parameters - Event parameters
 */
export function trackEvent(eventName, parameters = {}) {
  if (!isBrowser) return;

  const eventData = {
    event_category: parameters.category || 'user_interaction',
    event_label: parameters.label || '',
    value: parameters.value || 0,
    ...parameters,
    timestamp: new Date().toISOString(),
    path: window.location.pathname,
    url: window.location.href
  };

  // Google Analytics 4 (gtag)
  if (window.gtag) {
    window.gtag('event', eventName, eventData);
  }

  // Vercel Analytics (if available)
  if (window.va && typeof window.va === 'function') {
    window.va('track', eventName, eventData);
  }

  // Custom analytics (if needed)
  if (window.customAnalytics) {
    window.customAnalytics.track(eventName, eventData);
  }

  // Console logging in development
  if (import.meta?.env?.DEV) {
    console.log('📊 Analytics Event:', eventName, eventData);
  }
}

/**
 * Track page view
 * @param {string} path - Page path
 * @param {string} title - Page title
 */
export function trackPageView(path = null, title = null) {
  if (!isBrowser) return;

  const pagePath = path || window.location.pathname;
  const pageTitle = title || document.title;

  // Google Analytics 4
  if (window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      page_path: pagePath,
      page_title: pageTitle
    });
  }

  // Vercel Analytics
  if (window.va && typeof window.va === 'function') {
    window.va('pageview', {
      path: pagePath,
      title: pageTitle
    });
  }

  trackEvent('page_view', {
    category: 'navigation',
    label: pagePath,
    path: pagePath,
    title: pageTitle
  });
}

/**
 * Track button click
 * @param {string} buttonName - Name/ID of the button
 * @param {string} location - Where the button is located
 */
export function trackButtonClick(buttonName, location = 'unknown') {
  trackEvent('button_click', {
    category: 'engagement',
    label: buttonName,
    button_name: buttonName,
    location: location
  });
}

/**
 * Track form submission
 * @param {string} formName - Name/ID of the form
 * @param {boolean} success - Whether submission was successful
 */
export function trackFormSubmission(formName, success = true) {
  trackEvent('form_submit', {
    category: 'engagement',
    label: formName,
    form_name: formName,
    success: success,
    value: success ? 1 : 0
  });
}

/**
 * Track user journey step
 * @param {string} stepName - Name of the step
 * @param {number} stepNumber - Step number in the journey
 * @param {string} journeyName - Name of the journey (e.g., 'eligibility_checker')
 */
export function trackJourneyStep(stepName, stepNumber, journeyName) {
  trackEvent('journey_step', {
    category: 'user_journey',
    label: `${journeyName}:${stepName}`,
    step_name: stepName,
    step_number: stepNumber,
    journey_name: journeyName
  });
}

/**
 * Track user path through the site
 * @param {string} fromPath - Previous path
 * @param {string} toPath - Current path
 * @param {string} method - Navigation method ('click', 'back', 'forward', 'direct')
 */
export function trackNavigation(fromPath, toPath, method = 'click') {
  trackEvent('navigation', {
    category: 'user_journey',
    label: `${fromPath} → ${toPath}`,
    from_path: fromPath,
    to_path: toPath,
    method: method
  });
}

/**
 * Track error
 * @param {string} errorMessage - Error message
 * @param {string} errorType - Type of error
 * @param {Object} errorDetails - Additional error details
 */
export function trackError(errorMessage, errorType = 'unknown', errorDetails = {}) {
  trackEvent('error', {
    category: 'error',
    label: errorMessage,
    error_message: errorMessage,
    error_type: errorType,
    ...errorDetails
  });
}

/**
 * Track performance metrics
 * @param {string} metricName - Name of the metric
 * @param {number} value - Metric value
 * @param {string} unit - Unit of measurement
 */
export function trackPerformance(metricName, value, unit = 'ms') {
  trackEvent('performance', {
    category: 'performance',
    label: metricName,
    metric_name: metricName,
    value: value,
    unit: unit
  });
}

/**
 * Track Core Web Vitals
 */
export function trackWebVitals() {
  if (!isBrowser) return;

  // Track Largest Contentful Paint (LCP)
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        trackPerformance('LCP', lastEntry.renderTime || lastEntry.loadTime, 'ms');
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (error) {
      if (import.meta?.env?.DEV) {
        console.warn('LCP observer not supported:', error);
      }
    }

    // Track First Input Delay (FID)
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          trackPerformance('FID', entry.processingStart - entry.startTime, 'ms');
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (error) {
      if (import.meta?.env?.DEV) {
        console.warn('FID observer not supported:', error);
      }
    }

    // Track Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        trackPerformance('CLS', clsValue, 'score');
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      if (import.meta?.env?.DEV) {
        console.warn('CLS observer not supported:', error);
      }
    }
  }
}

// Initialize Web Vitals tracking on load
if (isBrowser) {
  if (document.readyState === 'complete') {
    trackWebVitals();
  } else {
    window.addEventListener('load', trackWebVitals);
  }
}

// Legacy compatibility exports
export const devLog = (import.meta?.env?.DEV) ? console.log : () => {};
export const devWarn = (import.meta?.env?.DEV) ? console.warn : () => {};
export const devError = console.error;
